import React, { useEffect } from "react";
import { Button, Col, Input, Row, message, Spin, Modal } from "antd";
import FormLayout, { FormSection, FormItem } from "Components/form";
import { Controller, useForm } from "react-hook-form";
import Yup from "ValidationModels/localizedYup";
import generatePassword from "Utils/generate-password";
import UserStoreModel, { ACTION_TYPES } from "store/models/user";
import { createComponentStore } from "easy-peasy";
import FetchSelect from "Components/fetch-select";
import { yupResolver } from "@hookform/resolvers";
import { DISPLAY_MODE } from "Constants";

const useUserStore = createComponentStore(UserStoreModel);

const PartnerFormValidationSchema = Yup.object().shape({
  name: Yup.string().required(),
  surname: Yup.string().required(),
  email: Yup.string().email().required(),
});

export default function UserForm({
  user,
  onAfterSuccess,
  loading = false,
  mode = DISPLAY_MODE.READ_ONLY,
  ...props
}) {
  const [store, actions] = useUserStore();

  const form = useForm({
    resolver: yupResolver(PartnerFormValidationSchema),
    defaultValues: user,
    mode: "onChange",
  });

  const { handleSubmit, errors, control, formState, reset } = form;

  useEffect(() => {
    if (user) reset(user);
  }, [user, reset]);

  useEffect(() => {
    if (store.user)
      switch (store.process) {
        case ACTION_TYPES.USER_CREATED:
          message.success("Kullanıcı oluşturuldu.");
          if (!onAfterSuccess) return;
          onAfterSuccess(user);
          break;
        case ACTION_TYPES.USER_UPDATED:
          message.success("Kullanıcı güncellendi.");
          if (!onAfterSuccess) return;
          onAfterSuccess(user);
          break;
        default:
          break;
      }
  }, [store.process, store.user]);

  const formOnSubmit = async (form) => {
    if (mode === DISPLAY_MODE.EDIT) {
      delete user.roles;
      if (user.usergroup.id !== form.usergroup.id) {
        actions.updateUserGroupItem({
          object: { id: form.usergroup.id },
          body: { groupId: { id: form.usergroup.id }, userId: { id: user.id } },
        });
      }
      await actions.updateUser({ ...user, ...form });
    }

    if (mode === DISPLAY_MODE.NEW) {
      form.password = generatePassword();
      await actions.createUser({ ...user, ...form });
    }
  };

  return (
    <Spin spinning={loading}>
      <form onSubmit={handleSubmit(formOnSubmit)} style={{ margin: "0 24px" }}>
        <FormLayout>
          <Col xs={24}>
            <FormSection title="Genel Bilgiler">
              <Row gutter={16}>
                <Col lg={12}>
                  <FormItem
                    htmlFor="name"
                    required={true}
                    error={errors.name}
                    label="Ad"
                  >
                    <Controller
                      control={control}
                      name="name"
                      as={
                        <Input
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          placeholder="Ad giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col lg={12}>
                  <FormItem
                    htmlFor="surname"
                    required={true}
                    error={errors.surname}
                    label="Soyad"
                  >
                    <Controller
                      control={control}
                      name="surname"
                      as={
                        <Input
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          placeholder="Soyad giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>

            <FormSection title="İletişim ve Birim Bilgileri">
              <Row gutter={16}>
                <Col lg={12}>
                  <FormItem
                    htmlFor="email"
                    error={errors.email}
                    required={true}
                    label="E-Posta"
                  >
                    <Controller
                      control={control}
                      name="email"
                      as={
                        <Input
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          placeholder="E-Posta giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>

                <Col lg={12}>
                  <FormItem htmlFor="phone" label="Telefon">
                    <Controller
                      control={control}
                      name="phone"
                      as={
                        <Input
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                          placeholder="Telefon giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>

                <Col lg={12}>
                  <FormItem htmlFor="userUnit" label="Kullanıcı Birimi">
                    <FetchSelect
                      api="/masterdata/userunits"
                      form={form}
                      field="userUnit"
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      placeholder="Kullanıcı birimi seçiniz"
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>

            {user ? (
              <FormSection title="Kullanıcı Grubu">
                <Row gutter={16}>
                  <Col lg={24}>
                    <FormItem>
                      <FetchSelect
                        api="/usergroups"
                        form={form}
                        field="usergroup"
                        readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        loadingActionType={ACTION_TYPES.FETCHING_USER_GROUPS}
                      />
                    </FormItem>
                  </Col>
                </Row>
              </FormSection>
            ) : null}

            {mode === DISPLAY_MODE.READ_ONLY ? null : (
              <Row
                justify="space-between"
                style={{
                  borderTop: "1px dashed rgb(166, 157, 152)",
                  position: "sticky",
                  bottom: 0,
                  padding: "16px 0",
                  backgroundColor: "#f2f2f2",
                }}
              >
                <Col>
                  <Button
                    loading={
                      store.process === ACTION_TYPES.UPDATING_USER ||
                      store.process === ACTION_TYPES.CREATING_USER
                    }
                    style={{ width: 120, marginRight: 8 }}
                    type="primary"
                    htmlType="submit"
                  >
                    Kaydet
                  </Button>
                  <Button
                    onClick={() => {
                      Modal.confirm({
                        title: "İptal",
                        content:
                          "Kaydetmediğiniz tüm değişiklikler, geri alınacaktır.",
                        okText: "Evet",
                        cancelText: "İptal",
                        onOk: () => {
                          reset();
                        },
                      });
                    }}
                  >
                    İptal
                  </Button>
                </Col>
              </Row>
            )}
          </Col>
        </FormLayout>
      </form>
    </Spin>
  );
}
